import {
  put,
  delay,
  takeEvery,
  all,
  call,
  select,
  race,
  cancelled,
} from "redux-saga/effects";
import _ from "lodash";
import moment from "moment-timezone";
import {
  setPageTopLoader,
  setByAreaDataSummaryTepco,
  setByAreaDataSummaryNtt,
  setByAreaDataSummaryNttFt,
  setByAreaDataSummaryHokuden,
  setByAreaDataTableTepco,
  setByAreaDataTableNtt,
  setByAreaDataTableNttFt,
  setByAreaDataTableHokuden,
} from "actions";
import { getJwtToken, getDataFromRDS } from "api";
import { PICTREE, ENDPOINT } from "../../constants";

const SXI_HEAVY_RETRY = {
  timeoutMs: 30000, // 30s
  retries: 3, // 合計 4 回までトライ（初回  リトライ3）
  backoffBase: 1500, // 1.5s指数バックオフ
};

const USERS_LIGHT_RETRY = {
  timeoutMs: 15000, // 15s
  retries: 1,
};

// タイムアウト＋リトライ
function* callWithTimeoutAndRetry(fn, args = [], opts = {}) {
  const {
    timeoutMs = 20000, // 1回の待ち時間（APIGW上限より少し短く）
    retries = 2, // 失敗時の再試行回数（合計3回＝初回＋2リトライ）
    backoffBase = 1000, // 1秒
    backoffFactor = 2, // 2倍ずつ
    jitter = true, // 少しランダムに待つ
    isRetryable = (err) => {
      const msg = (err && (err.message || err.errorMessage)) || "";
      return /timeout|timed out|504|gateway\s*time-?out/i.test(msg);
    },
  } = opts;

  let attempt = 0;
  while (true) {
    attempt += 1;

    try {
      // 1回の試行にタイムアウトをかける
      const { response, timeout } = yield race({
        response: call(fn, ...args),
        timeout: delay(timeoutMs),
      });

      if (timeout) throw new Error(`timeout after ${timeoutMs}ms`);
      return response; // 成功
    } catch (err) {
      if (attempt > retries || !isRetryable(err)) {
        throw err; // もう無理/リトライ不要
      }
      // 待ってから再試行（指数＋ジッター）
      const wait = Math.min(
        backoffBase * Math.pow(backoffFactor, attempt - 1),
        10000
      );
      const jitterMs = jitter ? Math.floor(Math.random() * (wait * 0.3)) : 0;
      yield delay(wait + jitterMs);
      // 次のループで再試行
    } finally {
      if (yield cancelled()) {
        // もし fetch/axios なら AbortController を仕込むとさらに良い
      }
    }
  }
}

function* runEffectsInBatches(effects, { concurrency = 2, gapMs = 300 } = {}) {
  const results = [];
  for (let i = 0; i < effects.length; i += concurrency) {
    const batch = effects.slice(i, i + concurrency);
    // 同時に投げるのは batch サイズ分だけ
    const batchResults = yield all(batch);
    results.push(...batchResults);

    // 次バッチの前にクールダウン（最後のバッチは不要）
    if (i + concurrency < effects.length && gapMs > 0) {
      yield delay(gapMs);
    }
  }
  return results;
}

// const createApprovedByAreaQuery = ({ startUtc, endUtc, seasonId }) => {
//   return {
//     query: `
//       WITH assets AS (
//         SELECT DISTINCT pa.id AS power_asset_id
//         FROM power_asset_state pas
//         JOIN power_asset pa ON pa.id = pas.power_asset_id
//         WHERE pas.game_space_id = '${seasonId}'
//       ),
//       asset_area_ranked AS (
//         SELECT
//           a.power_asset_id,
//           n.name AS area_name,
//           ROW_NUMBER() OVER (
//             PARTITION BY a.power_asset_id
//             ORDER BY paga.area_id ASC, n.name ASC
//           ) AS rn
//         FROM assets a
//         JOIN power_asset_game_space_area paga
//           ON paga.power_asset_id = a.power_asset_id
//         AND paga.game_space_id   = '${seasonId}'
//         JOIN game_space_area_name n
//           ON n.area_id = paga.area_id
//         AND n.language_code = 'JA'
//       ),
//       area_map AS (
//         SELECT power_asset_id, area_name
//         FROM asset_area_ranked
//         WHERE rn = 1
//       ),
//       approved_assets AS (
//         SELECT DISTINCT rr.power_asset_id
//         FROM review_request_event rre
//         JOIN review_request rr ON rr.id = rre.review_request_id
//         WHERE rre.type = 'APPROVED'
//           AND rre.occurred_datetime >= '${startUtc}'
//           AND rre.occurred_datetime <  '${endUtc}'
//           AND rre.game_space_id = '${seasonId}'
//       ),
//       per_area_registered AS (
//         SELECT m.area_name, COUNT(*) AS registered_count
//         FROM area_map m
//         GROUP BY m.area_name
//       ),
//       per_area_reviewok AS (
//         SELECT m.area_name, COUNT(DISTINCT aa.power_asset_id) AS reviewok_count
//         FROM approved_assets aa
//         JOIN area_map m ON m.power_asset_id = aa.power_asset_id
//         GROUP BY m.area_name
//       )
//       SELECT
//         r.area_name AS name,
//         r.registered_count AS count,
//         COALESCE(o.reviewok_count, 0) AS reviewok_count,
//         ROUND(COALESCE(o.reviewok_count, 0) / r.registered_count * 100, 2) AS achieved_percent
//       FROM per_area_registered r
//       LEFT JOIN per_area_reviewok o
//         ON o.area_name = r.area_name
//       ORDER BY r.registered_count DESC, r.area_name ASC;
//     `,
//     database: "KpiDashboard",
//     type: "list",
//   };
// };

const createApprovedByAreaQuery = ({ startUtc, endUtc, seasonId }) => {
  return {
    query: `
      WITH assets AS (
        SELECT /*+ SET_VAR(sort_buffer_size=262144) */
              DISTINCT pas.power_asset_id
        FROM power_asset_state pas
        WHERE pas.game_space_id = '${seasonId}'
      ),

      -- 代表エリア名をウィンドウ関数なしで確定（最小名を代表に）
      area_map AS (
        SELECT
          paga.power_asset_id,
          MIN(n.name) AS area_name
        FROM power_asset_game_space_area paga
        JOIN game_space_area_name n
          ON n.area_id = paga.area_id
        AND n.language_code = 'JA'
        JOIN assets a
          ON a.power_asset_id = paga.power_asset_id
        WHERE paga.game_space_id = '${seasonId}'
        GROUP BY paga.power_asset_id
      ),

      -- 承認付き RR を先に「資産×プレイヤ」で圧縮
      approved_rr AS (
        SELECT
          rr.power_asset_id,
          rr.player_id
        FROM review_request_event rre
        JOIN review_request rr
          ON rr.id = rre.review_request_id
        WHERE rre.type = 'APPROVED'
          AND rre.occurred_datetime >= '${startUtc}'
          AND rre.occurred_datetime <  '${endUtc}'
          AND rre.game_space_id = '${seasonId}'
        GROUP BY rr.power_asset_id, rr.player_id
      ),

      -- 登録（資産数）
      per_area_registered AS (
        SELECT am.area_name, COUNT(*) AS registered_count
        FROM area_map am
        GROUP BY am.area_name
      ),

      -- レビューOK（ユニーク資産）
      per_area_reviewok AS (
        SELECT am.area_name, COUNT(DISTINCT ar.power_asset_id) AS reviewok_count
        FROM approved_rr ar
        JOIN area_map am ON am.power_asset_id = ar.power_asset_id
        GROUP BY am.area_name
      ),

      -- 参加者（エリア内ユニークプレイヤ）
      per_area_participants AS (
        SELECT am.area_name, COUNT(DISTINCT ar.player_id) AS participants_count
        FROM approved_rr ar
        JOIN area_map am ON am.power_asset_id = ar.power_asset_id
        GROUP BY am.area_name
      ),

      -- 全体ユニーク参加者（エリア跨ぎ重複排除）
      global_participants AS (
        SELECT COUNT(DISTINCT player_id) AS total_participants
        FROM approved_rr
      )

      SELECT
        r.area_name AS name,
        r.registered_count AS count,
        COALESCE(o.reviewok_count, 0) AS reviewok_count,
        ROUND(COALESCE(o.reviewok_count, 0) / r.registered_count * 100, 2) AS achieved_percent,
        COALESCE(p.participants_count, 0) AS participants_count,
        gp.total_participants AS total_participants
      FROM per_area_registered r
      LEFT JOIN per_area_reviewok o  ON o.area_name = r.area_name
      LEFT JOIN per_area_participants p ON p.area_name = r.area_name
      CROSS JOIN global_participants gp
      ORDER BY r.registered_count DESC, r.area_name ASC;
    `,
    database: "KpiDashboard",
    type: "list",
  };
};

export const currentPage = (state) => state.page.currentPage;
export const dateRange = (state) => state.page.dateRangePicTree;
export const dateRangeNtt = (state) => state.page.dateRangePicTreeNtt;
export const dateRangeNttFt = (state) => state.page.dateRangePicTreeNttFt;
export const dateRangeHokuden = (state) => state.page.dateRangePicTreeHokuden;
export const selectedSeason = (state) => state.pictree.selectedSeason;
export const selectedSeasonNtt = (state) => state.pictreeNtt.selectedSeason;
export const selectedSeasonNttFt = (state) => state.pictreeNttFt.selectedSeason;
export const selectedSeasonHokuden = (state) => state.pictreeHokuden.selectedSeason;

export function* getByAreaData(action) {
  try {
    yield put(setPageTopLoader(true));
    yield put(setByAreaDataSummaryTepco([]));
    yield put(setByAreaDataTableTepco([]));
    yield put(setByAreaDataSummaryNtt([]));
    yield put(setByAreaDataSummaryNttFt([]));
    yield put(setByAreaDataTableNttFt([]));
    yield put(setByAreaDataSummaryHokuden([]));
    yield put(setByAreaDataTableHokuden([]));

    const jwtToken = yield call(getJwtToken);
    let selectedSeasonData = [];
    let rangeTarget = "";
    let seasonId = "";

    if (action.payload === "tepco") {
      rangeTarget = yield select(dateRange);
      selectedSeasonData = yield select(selectedSeason);

    } else if (action.payload === "ntt") {
      rangeTarget = yield select(dateRangeNtt);
      selectedSeasonData = yield select(selectedSeasonNtt);
    } else if (action.payload === "ntt-ft") {
      rangeTarget = yield select(dateRangeNttFt);
      selectedSeasonData = yield select(selectedSeasonNttFt);
    } else if (action.payload === "hokuden") {
      rangeTarget = yield select(dateRangeHokuden);
      selectedSeasonData = yield select(selectedSeasonHokuden);
    }
    seasonId = selectedSeasonData[0].seasonId;

    // JST日の境界 → UTC へ
    const startUtc = moment
      .tz(`${rangeTarget[0]} 00:00:00`, "Asia/Tokyo")
      .utc()
      .format("YYYY-MM-DD HH:mm:ss");
    const endUtc = moment
      .tz(`${rangeTarget[1]} 00:00:00`, "Asia/Tokyo")
      .add(1, "day")
      .utc()
      .format("YYYY-MM-DD HH:mm:ss");

    const byAreaSql = createApprovedByAreaQuery({ startUtc, endUtc, seasonId });

    const [rows] = yield call(runEffectsInBatches, [
      call(
        callWithTimeoutAndRetry,
        getDataFromRDS,
        [byAreaSql, ENDPOINT.GET_DATA_SXI_DENCHU, jwtToken],
        SXI_HEAVY_RETRY
      ),
    ]);

    const rawRows = Array.isArray(rows) ? rows : [];

    const table = rawRows.map((r) => {
      const registered = Number(r.count) || 0;
      const reviewOk = Number(r.reviewok_count) || 0;
      const unshot = Math.max(registered - reviewOk, 0);
      const achieved = registered
        ? _.round((reviewOk / registered) * 100, 2)
        : 0;
      const participants = Number(r.participants_count) || 0;

      return {
        name: r.name, // エリア名
        registered, // 登録数
        unshot, // 未撮影数
        reviewOk, // レビューOK数
        achievedPercent: achieved, // 達成率(%)
        participants, // 参加者数
      };
    });

    // 合計の集計
    const totals = table.reduce(
      (acc, row) => {
        acc.totalRegistered += row.registered;
        acc.totalReviewOk += row.reviewOk;
        acc.totalUnshot += row.unshot;
        return acc;
      },
      { totalRegistered: 0, totalReviewOk: 0, totalUnshot: 0 }
    );

    const overallAchievedPercent = totals.totalRegistered
      ? _.round((totals.totalReviewOk / totals.totalRegistered) * 100, 2)
      : 0;

    // “エリア数” 系（カード用）
    const registeredAreaCount = table.length; // 合計登録エリア数（行数）
    const unshotAreaCount = table.filter((r) =>
      (Number(r.reviewOk) || 0) === 0 || Number(r.registered) === Number(r.unshot)
    ).length;
    const totalParticipants =
      rawRows.length > 0 ? Number(rawRows[0].total_participants) || 0 : 0;

    const summary = {
      registeredAreaCount, // 合計登録エリア数
      unshotAreaCount, // 合計未撮影エリア数
      totalRegistered: totals.totalRegistered, // 合計登録数
      totalReviewOk: totals.totalReviewOk, // 合計レビューOK数
      totalAchievedPercent: overallAchievedPercent, // 合計達成率(%)
      totalParticipants,  // 合計ユニーク撮影者数
    };

    const tableForMui = table.map((row, index) => [
      index + 1,                        // #
      row.name,                         // エリア名
      row.registered,                   // 登録数
      row.unshot,                       // 未撮影数
      row.reviewOk,                     // レビューOK撮影数
      row.achievedPercent,              // 達成率(%)
      row.participants,                 // 参加者数
    ]);

    if (action.payload === "tepco") {
      yield put(setByAreaDataSummaryTepco(summary));
      yield put(setByAreaDataTableTepco(tableForMui));
    } else if (action.payload === "ntt") {
      yield put(setByAreaDataSummaryNtt(summary));
      yield put(setByAreaDataTableNtt(tableForMui));
    } else if (action.payload === "ntt-ft") {
      yield put(setByAreaDataSummaryNttFt(summary));
      yield put(setByAreaDataTableNttFt(tableForMui));
    } else if (action.payload === "hokuden") {
      yield put(setByAreaDataSummaryHokuden(summary));
      yield put(setByAreaDataTableHokuden(tableForMui));
    }
  } catch (err) {
    console.log("[getPictreeDataPhotoShootTrendByAsset] ERROR", err);
  } finally {
    yield put(setPageTopLoader(false));
  }
}

export default function* watchPageSettings() {
  yield takeEvery(PICTREE.GET_BY_AREA_DATA, getByAreaData);
}
